import { Component, OnInit } from '@angular/core';
import * as Highcharts from 'highcharts';
import { ActivatedRoute, Router } from '@angular/router';
import { DataService, SmartIrrigationService } from 'src/api/services';
import { DataAnalysisTimeSeriesValue, SmartIrrigationModel, SmartIrrigationRootNodeDto, SmartIrrigationWaterMeter } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { forkJoin, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

const DAY_MS = 24 * 60 * 60 * 1000;

@Component({
  selector: 'app-smart-irrigation-water-trend',
  templateUrl: './water-trend.component.html',
  styleUrls: ['./water-trend.component.scss']
})
export class SmartIrrigationWaterTrendComponent implements OnInit {
  Highcharts = Highcharts;
  chartConstructor = 'chart';

  nodeId: number;
  node: SmartIrrigationRootNodeDto;
  waterMeter: SmartIrrigationWaterMeter | null = null;
  loading = true;
  noData = false;
  lastRefresh: Date | null = null;

  hourlyChartOptions: Highcharts.Options | null = null;
  dailyChartOptions: Highcharts.Options | null = null;
  weeklyChartOptions: Highcharts.Options | null = null;
  monthlyChartOptions: Highcharts.Options | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private app: ApplicationConfigService,
    private irrigation: SmartIrrigationService,
    private dataService: DataService
  ) {}

  ngOnInit(): void {
    this.nodeId = +this.route.snapshot.paramMap.get('nodeId')!;
    this.load();
  }

  load(): void {
    this.loading = true;
    this.noData = false;

    forkJoin({
      model: this.irrigation.SmartIrrigationGetModel(this.app.customerId),
      node: this.irrigation.SmartIrrigationGetNode({ customerId: this.app.customerId, nodeId: this.nodeId })
    }).subscribe(({ model, node }) => {
      this.node = node;
      this.waterMeter = this.findWaterMeter(node, model);
      if (!this.waterMeter?.meterReadingDataUri) {
        this.loading = false;
        this.noData = true;
        return;
      }

      this.loadUsageSeries(this.waterMeter.meterReadingDataUri);
    }, () => {
      this.loading = false;
      this.noData = true;
    });
  }

  private findWaterMeter(node: SmartIrrigationRootNodeDto, model: SmartIrrigationModel): SmartIrrigationWaterMeter | undefined {
    const waterMeterDevice = (node.associatedDevices || []).find(device =>
      (device.deviceTypeName || '').toLowerCase().includes('water meter')
    );
    if (!waterMeterDevice) {
      return undefined;
    }
    const deviceUri = this.normalize(waterMeterDevice.uri);
    return Object.values(model?.waterMeters || {}).find(meter => this.normalize(meter.siteUri) === deviceUri);
  }

  private loadUsageSeries(uri: string): void {
    forkJoin({
      hourly: this.fetchUsageSeries(uri, '-7d:1h', 'hour'),
      daily: this.fetchUsageSeries(uri, '-30d:1d', 'day'),
      weekly: this.fetchUsageSeries(uri, '-90d:7d', 'week'),
      monthly: this.fetchUsageSeries(uri, '-365d:30d', 'month')
    }).subscribe(({ hourly, daily, weekly, monthly }) => {
      this.hourlyChartOptions = hourly.length > 0
        ? this.buildUsageChart(hourly, 'Hourly Usage', 7 * DAY_MS, DAY_MS, '{value:%d %b}', '#4299e1')
        : null;
      this.dailyChartOptions = daily.length > 0
        ? this.buildUsageChart(daily, 'Daily Usage', 30 * DAY_MS, 5 * DAY_MS, '{value:%d %b}', '#38a169')
        : null;
      this.weeklyChartOptions = weekly.length > 0
        ? this.buildUsageChart(weekly, 'Weekly Usage', 90 * DAY_MS, 14 * DAY_MS, '{value:%d %b}', '#d97706')
        : null;
      this.monthlyChartOptions = monthly.length > 0
        ? this.buildUsageChart(monthly, 'Monthly Usage', 365 * DAY_MS, 30 * DAY_MS, '{value:%b %Y}', '#a855f7')
        : null;
      this.loading = false;
      this.lastRefresh = new Date();
    }, () => {
      this.loading = false;
      this.noData = true;
    });
  }

  private fetchUsageSeries(uri: string, period: string, aggregatePeriod: string): Observable<DataAnalysisTimeSeriesValue[]> {
    if (!uri) {
      return of([]);
    }

    return this.dataService.DataGetData({
      customerId: this.app.customerId,
      request: {
        forceEmbed: true,
        index: 0,
        period,
        type: 'TimeSeries',
        timeZoneOffset: this.app.getTimeZoneOffset(null),
        timeSeries: {
          single: {
            variable: {
              uri,
              transforms: [{
                transform: { id: 'aggregate', name: 'Aggregate' },
                config: [
                  { name: 'algorithm', value: 'Change' },
                  { name: 'period', value: aggregatePeriod },
                  { name: 'tzConvert', value: 'false' }
                ]
              }]
            }
          }
        }
      }
    }).pipe(map(result => result?.values || []));
  }

  private buildUsageChart(
    values: DataAnalysisTimeSeriesValue[],
    seriesName: string,
    rangeMs: number,
    tickIntervalMs: number,
    dateFormat: string,
    color: string
  ): Highcharts.Options {
    const data: [number, number][] = [...values]
      .filter(value => value.value != null)
      .sort((a, b) => new Date(a.timeStamp).getTime() - new Date(b.timeStamp).getTime())
      .map(value => [new Date(value.timeStamp).getTime(), value.value]);
    const now = Date.now();
    const maxX = this.getMidnightTonightMs();

    return {
      chart: { type: 'column', backgroundColor: 'transparent', style: { fontFamily: 'inherit' }, spacing: [16, 16, 16, 16] },
      title: { text: undefined },
      xAxis: {
        type: 'datetime',
        min: maxX - rangeMs,
        max: maxX,
        tickInterval: tickIntervalMs,
        gridLineWidth: 1,
        plotLines: [{ value: now, color: 'rgba(255,255,255,0.45)', width: 1, dashStyle: 'Dash', zIndex: 4 }],
        labels: { format: dateFormat, rotation: -30 }
      },
      yAxis: { min: 0, title: { text: 'kL' } },
      tooltip: { valueSuffix: ' kL', xDateFormat: '%d %b %Y' },
      legend: { enabled: false },
      credits: { enabled: false },
      plotOptions: { column: { borderWidth: 0, pointPadding: 0.05, groupPadding: 0 } },
      series: [{ name: seriesName, type: 'column', data, color } as Highcharts.SeriesColumnOptions]
    };
  }

  private getMidnightTonightMs(): number {
    const tonight = new Date();
    tonight.setHours(24, 0, 0, 0);
    return tonight.getTime();
  }

  private normalize(value?: string): string {
    return (value || '').trim().toLowerCase();
  }

  goBack(): void {
    this.router.navigate(['/smart-irrigation/parks', this.nodeId]);
  }
}
